<div class="min-h-screen flex flex-col">
  <app-header [cartCount]="productService.getLocalCartProductLength()"></app-header>

  <div
    class="flex-1 bg-gradient-to-br from-slate-900 to-indigo-900 flex items-center justify-center p-4 md:p-6">
    <div class="w-full max-w-lg mx-auto relative">

      <!-- VERIFYING STATE -->
      <div *ngIf="state === PaymentState.VERIFYING"
        class="payment-card bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/40 overflow-hidden transition-all duration-300">

        <div class="p-6 sm:p-8 md:p-10">
          <div class="flex flex-col items-center text-center">

            <!-- Modern Spinner -->
            <div
              class="w-12 h-12 border-4 border-blue-200 border-t-blue-600 border-r-purple-500 rounded-full animate-spin mb-5">
            </div>

            <h2
              class="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-900 bg-clip-text text-transparent">
              Verifying Your Transaction
            </h2>
            <p class="text-gray-500 mt-2 text-sm sm:text-base">Please wait while we confirm your order.</p>

            <!-- Progress Bar -->
            <div class="w-full mt-8 mb-4">
              <div class="relative w-full h-2.5 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                <div
                  class="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 rounded-full transition-all duration-200 ease-out"
                  [style.width.%]="progressPercent"></div>
              </div>
            </div>

            <p class="text-gray-700 text-base font-medium mt-2 flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24"
                stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>This may take up to <strong class="text-indigo-600 text-lg font-bold mx-1">{{ remainingSeconds
                  }}</strong> seconds</span>
            </p>

            <p class="text-xs text-gray-400 mt-5 flex items-center gap-1 bg-gray-50 px-4 py-2 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24"
                stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Do not refresh or close this page.
            </p>
          </div>
        </div>
        <div class="bg-gray-50/80 px-6 py-3 border-t border-gray-100 text-center text-[11px] text-gray-400">
          🔒 Secure SSL Encrypted Payment
        </div>
      </div>

      <!-- ORDER PENDING STATE (Payment Successful) -->
      <div *ngIf="state === PaymentState.PAYMENT_DONE_ORDER_PENDING"
        class="payment-card bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-green-100/90 overflow-hidden transition-all duration-300">

        <div class="p-6 sm:p-8 md:p-10">
          <div class="flex flex-col items-center text-center">

            <!-- Success Icon -->
            <div
              class="w-16 h-16 bg-gradient-to-tr from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-9 w-9 text-white" fill="none" viewBox="0 0 24 24"
                stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h2 class="text-2xl sm:text-3xl font-extrabold text-gray-800">Payment Successful</h2>
            <p class="text-gray-600 mt-3 text-base leading-relaxed">
              Your payment was successful, but your order is still being processed.
            </p>

            <div class="mt-5 p-3 bg-amber-50 rounded-xl border border-amber-100 w-full">
              <p class="text-amber-800 text-sm flex items-center justify-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor" stroke-width="1.8">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                If the order is not confirmed within a few minutes, please contact support & check your payment history.
              </p>
            </div>

            <div class="mt-8 flex flex-col sm:flex-row gap-3 w-full">
              <button (click)="goToHome()"
                class="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-5 rounded-xl shadow-md transition-all active:scale-95 flex items-center justify-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Check Payment History
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Subtle footer -->
      <div class="text-center mt-8 text-white/50 text-xs flex justify-center gap-3">
        <span class="inline-flex items-center gap-1"><span class="w-1.5 h-1.5 bg-green-400 rounded-full"></span> Secure
          Payment</span>
        <span class="inline-flex items-center gap-1"><span class="w-1.5 h-1.5 bg-blue-400 rounded-full"></span> 256-bit
          SSL</span>
      </div>
    </div>
  </div>
</div>